// frontend/src/app/project-corpus/domain/project-corpus.service.ts
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EMPTY,
  Observable,
  Subject,
  Subscription,
  defer,
  forkJoin,
  interval,
  throwError,
} from 'rxjs';
import { catchError, finalize, switchMap, takeWhile, tap } from 'rxjs/operators';
import { derivePaperStatus } from './paper-status';
import { PROJECT_CORPUS_PORT } from '../ports/project-corpus.port';
import {
  CreateImportJobRequest,
  FailedUpload,
  GroupCorpusPaper,
  ImportJob,
  ImportSizeEstimate,
  Page,
  PageRequest,
  PaperListFilter,
  PaperMetadataPatch,
  ProjectPaper,
  ProjectSummary,
} from './types';

/** Default page request — 25 per page, sort by addedAt desc. */
const DEFAULT_PAGE: PageRequest = {
  page: 0,
  size: 25,
  sort: { field: 'addedAt', direction: 'desc' },
};

/** Polling interval, in milliseconds, while any import job is RUNNING. */
const POLL_INTERVAL_MS = 5000;

/**
 * Feature-scoped service that owns the reactive state for the project corpus
 * page. Mirrors `PdfCorpusService` in shape:
 *
 * - `@Injectable()` (no `providedIn`), provided at the page component level.
 * - Signal-based state for summary, jobs, papers, filter, page, active job.
 * - RxJS for HTTP via the injected `PROJECT_CORPUS_PORT`.
 * - Polling loop driven by `interval(5000)` while any job is `RUNNING`;
 *   `takeWhile` stops it after the first tick where no `RUNNING` jobs remain
 *   (with one final settling refetch via `inclusive: true`).
 * - `takeUntilDestroyed(injector)` guarantees no leaks past component destroy.
 * - Optimistic update + rollback on `editPaper` and `attachPdf`.
 */
@Injectable()
export class ProjectCorpusService {
  private readonly port = inject(PROJECT_CORPUS_PORT);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _summary = signal<ProjectSummary | null>(null);
  private readonly _importJobs = signal<Page<ImportJob> | null>(null);
  private readonly _papers = signal<Page<ProjectPaper> | null>(null);
  private readonly _currentFilter = signal<PaperListFilter>({});
  private readonly _currentPage = signal<PageRequest>(DEFAULT_PAGE);
  private readonly _activeJobId = signal<string | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  readonly summary = this._summary.asReadonly();
  readonly importJobs = this._importJobs.asReadonly();
  readonly papers = this._papers.asReadonly();
  readonly currentFilter = this._currentFilter.asReadonly();
  readonly currentPage = this._currentPage.asReadonly();
  readonly activeJobId = this._activeJobId.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly hasRunningJobs = computed(
    () => this._importJobs()?.items.some((j) => j.status === 'RUNNING') ?? false,
  );

  /** True when every paper has title+abstract+authors and at least one paper exists. */
  readonly canCompleteIngestion = computed(() => {
    const s = this._summary();
    return !!s && s.totalPapers > 0 && s.itemsNeedingAttention === 0;
  });

  private projectId: string | null = null;
  private pollSub: Subscription | null = null;
  private readonly refetchPapers$ = new Subject<void>();

  constructor() {
    this.refetchPapers$
      .pipe(
        switchMap(() => this.refetchPapers()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /**
   * Initialise for a given project: fetches summary + jobs + papers, then
   * starts polling if any RUNNING jobs are present in the initial response.
   */
  init(projectId: string): void {
    this.projectId = projectId;
    this.refetchAll().subscribe({
      next: () => {
        if (this.hasRunningJobs()) {
          this.startPolling();
        }
      },
    });
  }

  /** Updates the paper-list filter and refetches the current page of papers. */
  setFilter(filter: PaperListFilter): void {
    this._currentFilter.set(filter);
    // Reset to page 0 on filter change — same convention as PdfCorpus paginated lists.
    this._currentPage.set({ ...this._currentPage(), page: 0 });
    this.refetchPapers$.next();
  }

  /** Updates the paper-list page (size / sort / page index) and refetches. */
  setPage(page: PageRequest): void {
    this._currentPage.set(page);
    this.refetchPapers$.next();
  }

  /**
   * Sets (or clears) the active job filter. When set, the paper list is
   * scoped to that job's `importJobId`; when cleared, the filter is removed.
   */
  setActiveJobFilter(jobId: string | null): void {
    this._activeJobId.set(jobId);
    const next: PaperListFilter = { ...this._currentFilter() };
    if (jobId) {
      (next as { importJobId?: string }).importJobId = jobId;
    } else {
      delete (next as { importJobId?: string }).importJobId;
    }
    this.setFilter(next);
  }

  /**
   * Creates an import job through the port, then refetches summary + jobs +
   * papers and (re)starts polling if the new job is RUNNING.
   */
  createImportJob(request: CreateImportJobRequest): Observable<ImportJob> {
    if (!this.projectId) {
      return throwError(() => new Error('ProjectCorpusService not initialised'));
    }
    const projectId = this.projectId;
    return this.port.createImportJob(projectId, request).pipe(
      tap(() => {
        this.refetchAll().subscribe({
          next: () => {
            if (this.hasRunningJobs() && !this.pollSub) {
              this.startPolling();
            }
          },
        });
      }),
      this.trackError(),
    );
  }

  /**
   * Updates the title of an import job. Optimistic update with rollback
   * on failure. Confirms with server response on success.
   */
  updateTitle(jobId: string, title: string): Observable<ImportJob> {
    const snapshot = this._importJobs();
    // Optimistic update
    if (snapshot) {
      const next: Page<ImportJob> = {
        ...snapshot,
        items: snapshot.items.map((j) => (j.id === jobId ? { ...j, title } : j)),
      };
      this._importJobs.set(next);
    }
    return this.port.updateImportJobTitle(jobId, title).pipe(
      tap((updated) => {
        // Confirm with server state — only update title, the DTO may lack
        // other fields returned by the list endpoint.
        const current = this._importJobs();
        if (current) {
          this._importJobs.set({
            ...current,
            items: current.items.map((j) =>
              j.id === updated.id ? { ...j, title: updated.title } : j,
            ),
          });
        }
      }),
      catchError((err: unknown) => {
        if (snapshot) this._importJobs.set(snapshot);
        return throwError(() => err);
      }),
      this.trackError(),
    );
  }

  /**
   * Advances the project from ingestion to abstract screening through the port,
   * then refetches so the new state (and the read-only gating it drives) takes
   * effect across the page.
   * @param force When true, papers with incomplete metadata are removed and the
   *              project still advances.
   */
  advanceToScreening(force: boolean = false): Observable<void> {
    if (!this.projectId) {
      return throwError(() => new Error('ProjectCorpusService not initialised'));
    }
    return this.port.advanceToScreening(this.projectId, force).pipe(
      tap(() => this.refetchAll().subscribe()),
      this.trackError(),
    );
  }

  /**
   * Optimistically updates the matching paper row, then calls the port. On
   * error, rolls back to the pre-edit snapshot and rethrows.
   */
  editPaper(paperId: string, patch: PaperMetadataPatch): Observable<ProjectPaper> {
    const snapshot = this._papers();
    if (snapshot) {
      const next = this.applyPatchToPage(snapshot, paperId, patch);
      this._papers.set(next);
    }
    return this.port.editPaperMetadata(paperId, patch).pipe(
      tap((updated) => {
        // Confirm with server truth (handles fields the patch didn't include).
        const current = this._papers();
        if (current) {
          this._papers.set(this.replacePaperInPage(current, updated));
        }
        if (this.projectId) {
          this.port.getProjectSummary(this.projectId).subscribe({
            next: (s) => this._summary.set(s),
          });
        }
      }),
      catchError((err: unknown) => {
        if (snapshot) this._papers.set(snapshot);
        return throwError(() => err);
      }),
      this.trackError(),
    );
  }

  /**
   * Optimistically marks the paper's PDF as attached, then calls the port.
   * On error, rolls back to the pre-attach snapshot and rethrows.
   */
  attachPdf(paperId: string, file: File): Observable<ProjectPaper> {
    const snapshot = this._papers();
    if (snapshot) {
      const next = this.applyAttachToPage(snapshot, paperId);
      this._papers.set(next);
    }
    return this.port.attachPdf(paperId, file).pipe(
      tap((updated) => {
        const current = this._papers();
        if (current) {
          this._papers.set(this.replacePaperInPage(current, updated));
        }
      }),
      catchError((err: unknown) => {
        if (snapshot) this._papers.set(snapshot);
        return throwError(() => err);
      }),
      this.trackError(),
    );
  }

  /**
   * Pass-through to the port's OpenAlex preview endpoint. Returns an ephemeral
   * preview (total count + sample + duplicate count) used by the wizard before
   * the user confirms ingestion.
   */
  previewOpenAlex(
    query: string,
    workTypes: string[],
    sourceTypes: string[],
    yearFrom?: number,
    yearTo?: number,
    language?: string[],
  ): Observable<ImportSizeEstimate> {
    if (!this.projectId) {
      return throwError(() => new Error('ProjectCorpusService not initialised'));
    }
    return this.port.previewOpenAlex(query, workTypes, sourceTypes, yearFrom, yearTo, language);
  }

  /**
   * Pass-through to the port's group-corpus search endpoint. The group id is
   * resolved from the current project summary; the projectId is passed so the
   * adapter can flag papers `alreadyInProject`.
   */
  searchGroupCorpus(q: string, pageReq: PageRequest): Observable<Page<GroupCorpusPaper>> {
    if (!this.projectId) {
      return throwError(() => new Error('ProjectCorpusService not initialised'));
    }
    const groupId = this._summary()?.groupId ?? '';
    return this.port.searchGroupCorpus(groupId, q, this.projectId, pageReq);
  }

  retryExtraction(pdfId: string): Observable<void> {
    if (!this.projectId) return throwError(() => new Error('ProjectCorpusService not initialised'));
    return this.port
      .retryExtraction(pdfId, this.projectId)
      .pipe(tap(() => this.refetchAll().subscribe()));
  }

  listFailedUploads(jobId: string): Observable<FailedUpload[]> {
    return this.port.listFailedUploads(jobId);
  }

  deleteFailedUpload(jobId: string, failureId: string): Observable<void> {
    return this.port.deleteFailedUpload(jobId, failureId);
  }

  /**
   * Undoes a completed import job during SETUP/INGESTION. On success,
   * refetches summary + jobs + papers to reflect the reverted state.
   */
  undoImportJob(jobId: string): Observable<void> {
    if (!this.projectId) {
      return throwError(() => new Error('ProjectCorpusService not initialised'));
    }
    return this.port.undoImportJob(jobId).pipe(
      tap(() => this.refetchAll().subscribe()),
      this.trackError(),
    );
  }

  listPapersForJob(jobId: string): Observable<Page<ProjectPaper>> {
    if (!this.projectId) return throwError(() => new Error('ProjectCorpusService not initialised'));
    return this.port.listPapers(this.projectId, { importJobId: jobId }, { page: 0, size: 200 });
  }

  /** Manually clears the last-error signal. */
  clearError(): void {
    this._error.set(null);
  }

  // ────────────────────────────────────────────────────────────────────────
  //   Internals
  // ────────────────────────────────────────────────────────────────────────

  /**
   * Starts the 5s polling loop. Each tick refetches summary + jobs + papers
   * (current page only). `takeWhile(..., true)` keeps emitting until the
   * predicate becomes false *and* lets the final non-matching tick through,
   * so we get one settling refetch after the last RUNNING job completes.
   */
  private startPolling(): void {
    if (this.pollSub) return;
    this.pollSub = interval(POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.refetchAll()),
        // `inclusive: true` makes the predicate run AFTER the emission and
        // lets the first "no RUNNING jobs" emission pass through before the
        // stream completes. Net effect: one final settling refetch after the
        // last RUNNING job transitions, then we stop polling.
        takeWhile(() => this.hasRunningJobs(), true),
        finalize(() => {
          this.pollSub = null;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /** Fetches summary + jobs + papers for the current project in parallel. */
  private refetchAll(): Observable<{
    summary: ProjectSummary;
    jobs: Page<ImportJob>;
    papers: Page<ProjectPaper>;
  }> {
    return defer(() => {
      if (!this.projectId) return EMPTY;
      const projectId = this.projectId;
      this._loading.set(true);
      return forkJoin({
        summary: this.port.getProjectSummary(projectId),
        jobs: this.port.listImportJobs(projectId, { page: 0, size: 50 }),
        papers: this.port.listPapers(projectId, this._currentFilter(), this._currentPage()),
      }).pipe(
        tap(({ summary, jobs, papers }) => {
          this._summary.set(summary);
          this._importJobs.set(jobs);
          this._papers.set(papers);
        }),
        catchError((err: unknown) => {
          this._error.set(this.toMessage(err));
          return throwError(() => err);
        }),
        finalize(() => this._loading.set(false)),
      );
    });
  }

  /** Refetches just the paper list (used by filter / page changes). */
  private refetchPapers(): Observable<Page<ProjectPaper>> {
    return defer(() => {
      if (!this.projectId) return EMPTY;
      const projectId = this.projectId;
      this._loading.set(true);
      return this.port.listPapers(projectId, this._currentFilter(), this._currentPage()).pipe(
        tap((papers) => {
          this._papers.set(papers);
          const page = this._currentPage().page;
          if (page > 0 && papers.totalPages > 0 && page >= papers.totalPages) {
            const lastPage = papers.totalPages - 1;
            this._currentPage.set({ ...this._currentPage(), page: lastPage });
            this.refetchPapers$.next();
          }
        }),
        catchError((err: unknown) => {
          this._error.set(this.toMessage(err));
          return throwError(() => err);
        }),
        finalize(() => this._loading.set(false)),
      );
    });
  }

  private applyPatchToPage(
    page: Page<ProjectPaper>,
    paperId: string,
    patch: PaperMetadataPatch,
  ): Page<ProjectPaper> {
    const items = page.items.map((p) => (p.id === paperId ? this.applyPatchToPaper(p, patch) : p));
    return { ...page, items };
  }

  private applyPatchToPaper(paper: ProjectPaper, patch: PaperMetadataPatch): ProjectPaper {
    const merged: ProjectPaper = {
      ...paper,
      title: patch.title ?? paper.title,
      authors: patch.authors ?? paper.authors,
      year: patch.year === undefined ? paper.year : patch.year,
      doi: patch.doi === undefined ? paper.doi : patch.doi,
      abstractText: patch.abstractText === undefined ? paper.abstractText : patch.abstractText,
      venue: patch.venue === undefined ? paper.venue : patch.venue,
      workType: patch.workType === undefined ? paper.workType : patch.workType,
      sourceType: patch.sourceType === undefined ? paper.sourceType : patch.sourceType,
    };
    const status =
      merged.status === 'DUPLICATE_SKIPPED'
        ? merged.status
        : derivePaperStatus({
            title: merged.title,
            abstractText: merged.abstractText,
            authors: merged.authors,
            isDuplicate: false,
          });
    return { ...merged, status };
  }

  private applyAttachToPage(page: Page<ProjectPaper>, paperId: string): Page<ProjectPaper> {
    const items = page.items.map((p) =>
      p.id === paperId
        ? {
            ...p,
            hasPdf: true,
            status: p.status === 'NEEDS_PDF' ? ('COMPLETE' as const) : p.status,
          }
        : p,
    );
    return { ...page, items };
  }

  private replacePaperInPage(page: Page<ProjectPaper>, updated: ProjectPaper): Page<ProjectPaper> {
    const items = page.items.map((p) => (p.id === updated.id ? updated : p));
    return { ...page, items };
  }

  private toMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    return String(err);
  }

  private trackError<T>(): (source: Observable<T>) => Observable<T> {
    return (source) =>
      source.pipe(
        catchError((err: unknown) => {
          this._error.set(this.toMessage(err));
          return throwError(() => err);
        }),
      );
  }
}
